import Checkbox from '@allesauto/core/checkbox';
import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import Slider from '@allesauto/core/slider';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { DropdownOpenHeight } from '@allesauto/utils/consts';
import { getSlug } from '@allesauto/utils/funcs';
import debounce from 'lodash/debounce';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { EmissionFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/emission';

export type EmissionFilterProps = {
  emissionClass: SchemaTypes.EmissionClass[];
  emissionSticker: SchemaTypes.EmissionSticker[];
  co2Efficiency: SchemaTypes.Co2Efficiency[];
  minCo2Emission: SchemaTypes.EmissionFacet['minCo2Emission'];
  maxCo2Emission: SchemaTypes.EmissionFacet['maxCo2Emission'];
  particleFilter: SchemaTypes.EmissionClass;
  value: EmissionFilterStateProps;
  onChange: (value: EmissionFilterStateProps) => void;
};

const EmissionFilter: FC<React.PropsWithChildren<EmissionFilterProps>> = ({
  emissionClass = [],
  emissionSticker = [],
  co2Efficiency = [],
  minCo2Emission: minCo2EmissionDefault,
  maxCo2Emission: maxCo2EmissionDefault,
  particleFilter = {
    name: 'ja',
    displayName: 'Ja',
  },
  value,
  onChange,
}) => {
  const intl = useIntl();
  const trackingIdentifierEmission = 'aa-filter--emission';
  const trackingIdentifierEmissionClass = `${trackingIdentifierEmission}-class`;
  const trackingIdentifierEmissionCo2 = `${trackingIdentifierEmission}-co2`;
  const trackingIdentifierEmissionFilter = `${trackingIdentifierEmission}-filter`;
  const trackingIdentifierEmissionEfficiency = `${trackingIdentifierEmission}-efficiency`;
  const trackingIdentifierEmissionSticker = `${trackingIdentifierEmission}-sticker`;

  const transformListToSearchDropdown = (
    values: SchemaTypes.EmissionClass[] | SchemaTypes.Co2Efficiency[]
  ): Option[] => {
    const searchDropdownList =
      values &&
      values.map((value) => ({
        value: value.name,
        label: value.displayName,
      }));
    return searchDropdownList;
  };

  const emissionClassForSearchDropdown: Option[] =
    transformListToSearchDropdown(emissionClass);

  const co2EfficiencyForSearchDropdown: Option[] =
    transformListToSearchDropdown(co2Efficiency)
      .sort((value1, value2) => {
        if (value1.label < value2.label) return -1;
        if (value1.label > value2.label) return 1;
        return 0;
      })
      .sort((value1, value2) => {
        return String(value2.label).length - String(value1.label).length;
      });

  const tooltipFormatter = (value: number | number[] | null) => {
    const translationTooltip = translate(
      intl,
      'ui.PCP.FILTERS.FUEL_TYPE.FUEL_CONSUMPTION_SLIDER_STEP_LABEL'
    );

    return value !== null && !Array.isArray(value)
      ? `${translationTooltip} ${value}`
      : null;
  };

  const maxCo2EmissionChange = useCallback(
    debounce((v) => onChange({ maxCo2Emission: v as number }), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const emissionClassDropDownChange = useCallback(
    (options: Option | readonly Option[] | null) => {
      const val = (options && (options as Option).value) || null;
      onChange({ emissionClass: (val as string) || null });
    },
    [onChange]
  );

  const co2EfficiencyDropDownChange = useCallback(
    (options: Option | readonly Option[] | null) => {
      const val = (options && (options as Option).value) || null;
      onChange({ co2Efficiency: (val as string) || null });
    },
    [onChange]
  );

  const handleFilterParticle = useCallback(
    (checked: boolean) => {
      onChange({ particleFilter: checked });
    },
    [onChange]
  );

  const handleCheckboxChange = useCallback(
    (item: string, checked: boolean) => {
      const emissionSticker = [...(value.emissionSticker ?? [])];

      if (checked) {
        emissionSticker.push(item);
      } else {
        emissionSticker.splice(emissionSticker.indexOf(item), 1);
      }

      onChange({ emissionSticker });
    },
    [value, onChange]
  );

  return (
    <>
      <p className="font-weight-normal pb-10px pt-0 text-p1 text-white">
        {translate(
          intl,
          'ui.PCP.FILTERS.EMISSIONS.EMISSION_CLASS_DROP_DOWN_LABEL'
        )}
      </p>
      <div className="mb-40px">
        <SearchDropdown
          id={trackingIdentifierEmissionClass}
          placeholder={translate(intl, 'ui.PCP.FILTERS.FILTER_PLACEHOLDER')}
          options={emissionClassForSearchDropdown}
          darkBg={true}
          isMulti={false}
          hasInfoIcon={false}
          isSearchable={false}
          headlinePosition="top"
          value={
            (value?.emissionClass &&
              emissionClassForSearchDropdown.find(
                (item) => item.value === value.emissionClass
              )) ||
            null
          }
          onChange={(value: Option | readonly Option[] | null) =>
            emissionClassDropDownChange(value)
          }
          context="relative-position"
          openHeight={DropdownOpenHeight.MEDIUM}
        />
      </div>
      <p className="font-weight-normal pb-10px text-p1 text-white">
        {translate(intl, 'ui.PCP.FILTERS.EMISSIONS.STICKER_LABEL')}
      </p>
      {emissionSticker.map((es, i) => {
        return (
          <Checkbox
            id={`${trackingIdentifierEmissionSticker}-type-${getSlug(es.name)}`}
            key={i}
            label={es.displayName}
            checked={
              value?.emissionSticker && value.emissionSticker.includes(es.name)
            }
            onChange={(v) => handleCheckboxChange(es.name, v)}
            className="!flex pb-25px"
            darkBg
          />
        );
      })}
      <p className="font-weight-normal pb-10px pt-20px text-p1 text-white">
        {translate(intl, 'ui.PCP.FILTERS.EMISSIONS.FILTER_PARTICLE_LABEL')}
      </p>
      <Checkbox
        id={trackingIdentifierEmissionFilter}
        label={particleFilter.displayName}
        onChange={(v) => {
          handleFilterParticle(v);
        }}
        checked={value?.particleFilter}
        className="!flex pb-[50px]"
        darkBg
      />
      <p className="font-weight-normal pb-10px text-[16px] text-white">
        {translate(intl, 'ui.PCP.FILTERS.EMISSIONS.CO2_EMISSIONS_LABEL')}
      </p>
      <div className="-mt-20px pb-40px w-[98%]">
        <Slider
          id={trackingIdentifierEmissionCo2}
          min={minCo2EmissionDefault}
          max={maxCo2EmissionDefault}
          value={[value?.maxCo2Emission ?? maxCo2EmissionDefault]}
          onChange={maxCo2EmissionChange}
          rangeLabelText={translate(
            intl,
            'ui.PCP.FILTERS.EMISSIONS.EMISSION_SLIDER_STEP_LABEL'
          )}
          step={1}
          darkBg={true}
          range={false}
          tooltipFormatter={tooltipFormatter}
        />
      </div>
      <p className="font-weight-normal pb-10px text-p1 text-white">
        {translate(
          intl,
          'ui.PCP.FILTERS.EMISSIONS.CO2_EMISSIONS_EFFICIENCY_LABEL'
        )}
      </p>
      <div className="mb-20px">
        <SearchDropdown
          id={trackingIdentifierEmissionEfficiency}
          placeholder={translate(intl, 'ui.PCP.FILTERS.FILTER_PLACEHOLDER')}
          options={co2EfficiencyForSearchDropdown}
          darkBg={true}
          isMulti={false}
          hasInfoIcon={false}
          isSearchable={false}
          headlinePosition="top"
          value={
            (value?.co2Efficiency &&
              co2EfficiencyForSearchDropdown.find(
                (item) => item.value === value.co2Efficiency
              )) ||
            null
          }
          onChange={(value: Option | readonly Option[] | null) =>
            co2EfficiencyDropDownChange(value)
          }
          context="relative-position"
          openHeight={DropdownOpenHeight.MEDIUM}
        ></SearchDropdown>
      </div>
    </>
  );
};

export default EmissionFilter;
